// React imports
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

//  material-ui imports
import { Stack, TextField, Typography } from '@mui/material';

// project imports
import gestopAPI from 'utils/gestopAPI';
import FileMngrDialog from './FileMngrDialog';
import { isSystemFolder } from '..';
import { getFileMngrErrorMessage, getFileMngrRenameBaseName, isFileMngrResponseSuccessful } from '../utils/fileMngrPath';

// third-party imports
import { useIntl } from 'react-intl';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

// ==============================|| FILEMNGR DIALOG RENAME ||============================== //

/**
 * FileMngrDialogRename component - Responsible for renaming folders and files in the file manager dialog.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {boolean} props.openRenameDialog - Flag to control the visibility of the rename dialog.
 * @param {Function} props.handleCloseRenameDialog - Callback function to handle the close event of the rename dialog.
 * @param {Function} props.handleRefreshFolder - Callback function to handle the refresh event of the folder.
 * @param {string} props.currentPath - The current path of the folder.
 * @param {string} props.selectedItem - The selected item to rename.
 * @param {string} props.url - The URL for the API.
 * @returns {JSX.Element} The JSX element representing the rename dialog component.
 */
const FileMngrDialogRename = ({ openRenameDialog, handleCloseRenameDialog, handleRefreshFolder, currentPath, selectedItem, url }) => {
    const [newName, setNewName] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const intl = useIntl();
    const dispatch = useDispatch();

    const newNameWithoutExt = getFileMngrRenameBaseName(selectedItem);
    const normalizedNewName = typeof newName === 'string' ? newName.trim() : '';

    const handleRename = async () => {
        const normalizedName = normalizedNewName;

        if (newNameWithoutExt !== normalizedName) {
            try {
                setIsLoading(true);

                const path = currentPath;
                const action = 'rename';
                const data = selectedItem;
                const name = selectedItem?.name;

                const response = await gestopAPI.post(url, {
                    action,
                    data,
                    name,
                    newName: normalizedName + (selectedItem?.isFile && selectedItem?.type ? `.${selectedItem?.type}` : ''),
                    path,
                    showFileExtension: true
                });

                if (isFileMngrResponseSuccessful(response)) {
                    await handleRefreshFolder();
                    handleCloseRenameDialog();
                    setNewName('');
                } else {
                    dispatch(
                        openSnackbar({
                            open: true,
                            message: getFileMngrErrorMessage(response) || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                            variant: 'alert',
                            alert: { color: 'error' }
                        })
                    );
                }
            } catch (error) {
                dispatch(
                    openSnackbar({
                        open: true,
                        message: error?.message || intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-errmsg-errranaming' }),
                        variant: 'alert',
                        alert: { color: 'error' }
                    })
                );
            } finally {
                setIsLoading(false);
            }
        } else {
            dispatch(
                openSnackbar({
                    open: true,
                    message: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-errmsg-nochanges' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        }
    };

    useEffect(() => {
        setNewName(openRenameDialog ? newNameWithoutExt : '');
    }, [openRenameDialog, newNameWithoutExt]);

    return (
        <FileMngrDialog
            title={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-title' })}
            isOpen={openRenameDialog}
            onClose={handleCloseRenameDialog}
            buttons={
                normalizedNewName !== '' && normalizedNewName !== newNameWithoutExt
                    ? [
                          {
                              text: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-button-text' }),
                              onClick: handleRename,
                              variant: 'contained',
                              color: 'warning',
                              disabled: isLoading || isSystemFolder(normalizedNewName),
                              isLoading
                          }
                      ]
                    : []
            }
        >
            <Stack spacing={1.5} justifyContent="center" alignItems="center" width={320} p={1}>
                <TextField
                    label={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-textfield-label' })}
                    value={typeof newName === 'string' ? newName : ''}
                    onChange={(e) => setNewName(e.target.value)}
                    variant="outlined"
                    fullWidth
                />
                <Typography color="error" sx={{ display: isSystemFolder(normalizedNewName) ? 'block' : 'none' }}>
                    {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-renamefile-label-systemfolder' })}
                </Typography>
            </Stack>
        </FileMngrDialog>
    );
};

FileMngrDialogRename.propTypes = {
    openRenameDialog: PropTypes.bool,
    handleCloseRenameDialog: PropTypes.func,
    handleRefreshFolder: PropTypes.func,
    currentPath: PropTypes.string,
    selectedItem: PropTypes.object,
    url: PropTypes.string
};

export default FileMngrDialogRename;
